import { z } from 'zod';
import { type ChatDateFilter } from '../enums/index.js';
import type { KolProfile } from './kol.profile.js';
import { recommendationReasonSchema } from './kol.profile.js';
import { type ICoords } from './location.js';
import type { ConfigDrivenProfileMessage } from './profile-message.js';

export enum KolListTypeEnum {
  Recommendations = 'recommendations',
}

export const KolListType = z.enum(KolListTypeEnum);
export type KolListType = z.infer<typeof KolListType>;

export const KolSearchResultModel = z.object({
  id: z.coerce.number(),
  isKol: z.boolean(),
  affiliations: z.array(
    z.object({
      // id: z.coerce.number(),
      name: z.string(),
      city: z.string().nullish(),
      state: z.string().nullish(),
      street: z.string().nullish(),
      country: z.string().nullish(),
      role: z.string().nullish(),
      zip: z.string().nullish(),
    })
  ),
  name: z.string(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  email: z.string().nullable(),
  phone: z.string().nullish(),
  qualifications: z.array(z.string()),
  specialties: z.array(z.string()),

  city: z.string().nullish(),
  state: z.string().nullish(),
  streetAddress: z.string().nullish(),
  country: z.string().nullish(),
  zipCode: z.string().nullish(),

  distance: z.number().nullish(),
  locationConfidence: z.number().nullish(),
  locationCoords: z
    .object({
      latitude: z.number(),
      longitude: z.number(),
    })
    .nullable()
    .optional(),
  locationName: z.string().nullable().optional(),

  recommendations: z.array(recommendationReasonSchema).nullable().optional(),
});
export type KolSearchResult = z.infer<typeof KolSearchResultModel>;

export type KolSearchRecommendation = KolSearchResult;

export type InteractionsTimeRangeMetadata = {
  numDocumentedInterests: number;
  numContactCenterInteractions: number;
  numFbmsInteractions: number;
  numMedicalInsights: number;
  withMe: number;
  total: number;
};

export type InteractionsMetadata = {
  allTime: InteractionsTimeRangeMetadata;
  lookback: InteractionsTimeRangeMetadata;
  lookbackPeriod: ChatDateFilter;
} & Partial<InteractionsTimeRangeMetadata>;

export type KolProfileMessage = ConfigDrivenProfileMessage & {
  affiliations: KolProfile.Affiliation[];
  interactions: InteractionsMetadata;
  keyDiscussionQuestions: string[];
  keyInsights: string[];
  keyFocusAreas?: KolProfile.ScientificFocusArea[];

  numClinicalTrials: number;
  numLeadAuthorPublications: number;
  numPublications: number;
  numCongressAbstracts: number;

  numScientificFocusAreas: number;
  numClinicalFocusAreas: number;
  numDigitalFocusAreas: number;

  specialty: string;
  title: string | null;
  qualifications: string[];
  recommendations: KolProfile.RecommendationReason[];
  events: GroupedKolEvent[];
  upcomingEvents?: KolProfile.Event[];

  contact: {
    address: string | null;
    email: string | null;
    phone: string | null;
  };
  locationCoords: ICoords | null;

  availabilityInsights?: AvailabilityInsights;
};

export type GroupedKolEvent = {
  id: number;
  name: string;
  startDate: Date;
  endDate: Date | null;
  roles: {
    position: string | null;
    talkTitle: string | null;
  }[];
};

export const AvailabilityInsightsSchema = z.object({
  totalInteractions: z.number(),
  disclaimer: z.string(),
  insights: z.record(
    z.string(),
    z.object({
      count: z.number(),
      pct: z.number(),
      rank: z.enum(['HIGH', 'MEDIUM', 'LOW', 'ZERO']),
    })
  ),
});

export type AvailabilityInsights = z.infer<typeof AvailabilityInsightsSchema>;
